// Shared fleet filter: one search box + tag chips + "online only" + optional
// group-by, reused by every agent list (dashboard, new-session picker, tunnels,
// admin). Tags are `key:value` strings (`env:prod`, `client:acme`); the key
// before the colon is the grouping axis, so a hierarchy is a *view* over flat
// tags rather than stored structure.
//
// With `syncRoute`, the state lives in the URL (`?q=&tags=a,b&online=1&group=env`)
// so a filtered view is a shareable link.

import { computed, reactive, ref, watch, type Ref } from "vue";
import { useRoute, useRouter } from "vue-router";

import type { AgentView } from "@/api/types";

/** `env:prod` → `env`; untyped tags have no key. */
export function tagKey(tag: string): string {
  const i = tag.indexOf(":");
  return i > 0 ? tag.slice(0, i) : "";
}

/** `env:prod` → `prod`; untyped tags return themselves. */
export function tagValue(tag: string): string {
  const i = tag.indexOf(":");
  return i > 0 ? tag.slice(i + 1) : tag;
}

/** Parse a free-form tag string ("env:prod, client:acme role:db") into a list. */
export function parseTags(input: string): string[] {
  return input
    .split(/[\s,]+/)
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
}

export interface AgentGroup {
  /** Group value (`prod`), or "" for the ungrouped / untagged bucket. */
  key: string;
  agents: AgentView[];
}

export function useAgentFilter(agents: Ref<AgentView[]>, opts: { syncRoute?: boolean } = {}) {
  const query = ref("");
  const tags = ref<string[]>([]);
  const onlineOnly = ref(false);
  const groupBy = ref("");

  if (opts.syncRoute) {
    const route = useRoute();
    const router = useRouter();
    const q = route.query;
    query.value = typeof q.q === "string" ? q.q : "";
    tags.value = typeof q.tags === "string" ? parseTags(q.tags) : [];
    onlineOnly.value = q.online === "1";
    groupBy.value = typeof q.group === "string" ? q.group : "";
    watch([query, tags, onlineOnly, groupBy], () => {
      const next: Record<string, string> = {};
      if (query.value) next.q = query.value;
      if (tags.value.length) next.tags = tags.value.join(",");
      if (onlineOnly.value) next.online = "1";
      if (groupBy.value) next.group = groupBy.value;
      void router.replace({ query: next });
    });
  }

  const allTags = computed(() => {
    const set = new Set<string>();
    for (const a of agents.value) for (const t of a.tags ?? []) set.add(t);
    return [...set].sort();
  });

  const tagKeys = computed(() => {
    const set = new Set<string>();
    for (const t of allTags.value) {
      const k = tagKey(t);
      if (k) set.add(k);
    }
    return [...set].sort();
  });

  function matches(a: AgentView): boolean {
    if (onlineOnly.value && a.presence !== "online") return false;
    const have = a.tags ?? [];
    for (const t of tags.value) if (!have.includes(t)) return false;
    const q = query.value.trim().toLowerCase();
    if (!q) return true;
    return (
      a.login.toLowerCase().includes(q) ||
      a.hostname.toLowerCase().includes(q) ||
      (a.display_name?.toLowerCase().includes(q) ?? false) ||
      have.some((t) => t.includes(q))
    );
  }

  const filtered = computed(() => agents.value.filter(matches));

  const groups = computed<AgentGroup[]>(() => {
    const key = groupBy.value;
    if (!key) return [{ key: "", agents: filtered.value }];
    const buckets = new Map<string, AgentView[]>();
    for (const a of filtered.value) {
      const tag = (a.tags ?? []).find((t) => tagKey(t) === key);
      const value = tag ? tagValue(tag) : "";
      const list = buckets.get(value) ?? [];
      list.push(a);
      buckets.set(value, list);
    }
    // Named groups sorted, the untagged bucket last.
    return [...buckets.entries()]
      .sort(([a], [b]) => (a === "" ? 1 : b === "" ? -1 : a.localeCompare(b)))
      .map(([k, list]) => ({ key: k, agents: list }));
  });

  function toggleTag(tag: string) {
    tags.value = tags.value.includes(tag)
      ? tags.value.filter((t) => t !== tag)
      : [...tags.value, tag];
  }

  // Setters (not direct assignment) so the bar component can drive the shared
  // state without mutating a prop (vue/no-mutating-props).
  function setQuery(v: string) {
    query.value = v;
  }
  function setOnlineOnly(v: boolean) {
    onlineOnly.value = v;
  }
  function setGroupBy(v: string) {
    groupBy.value = v;
  }

  function clear() {
    query.value = "";
    tags.value = [];
    onlineOnly.value = false;
  }

  const active = computed(() => Boolean(query.value) || tags.value.length > 0 || onlineOnly.value);

  return reactive({
    query,
    tags,
    onlineOnly,
    groupBy,
    allTags,
    tagKeys,
    filtered,
    groups,
    active,
    total: computed(() => agents.value.length),
    toggleTag,
    setQuery,
    setOnlineOnly,
    setGroupBy,
    clear,
  });
}

export type AgentFilter = ReturnType<typeof useAgentFilter>;
